import axios from 'axios'
import { BASE_API_PATH, TOKEN_KEY } from '@/utils/variable'
import { notification as Notification, Modal } from 'jetlinkscky-ui-components'
import router from '@/router'
import { LoginPath } from '@/router/menu'
import { cleanToken, getToken, LocalStore, md5Js } from '@/utils/comm'
import type { AxiosInstance, AxiosResponse } from 'axios'

interface AxiosResponseRewrite<T = any[]> extends AxiosResponse<T, any> {
  result: T
  success: boolean
}

export const SUCCESS_CODE = 200 // Success code

const filterApiUrl = [
  '/system/version',
  '/system/config/front',
  '/authorize/captcha/config',
  '/application/sso/_all',
  '/authorize/captcha/image',
  '/application/sso/bind-code',
  '/authorize/login',
  '/application/',
  '/license'
]

export const request = axios.create({
  withCredentials: false,
  baseURL: BASE_API_PATH,
  timeout: 1000 * 15
})

/**
 * Make a POST request
 * @param {String} url - The request URL
 * @param {Object} [data] - The request payload
 * @param {String} responseType - Set to 'blob' if the response is a file stream
 * @param {Object|String} [ext] - Additional configuration options including headers
 * @returns {AxiosInstance} - Axios response instance
 */
export const post = function <T>(url: string, data = {}, params = {}, ext = {}) {
  ext = typeof ext === 'string' ? { responseType: ext } : ext
  return request<any, AxiosResponseRewrite<T>>({
    ...ext,
    params,
    method: 'POST',
    url,
    data
  })
}

/**
 * Make a PUT request
 * @param {String} url - The request URL
 * @param {Object} [data] - The request payload
 * @returns {AxiosInstance} - Axios response instance
 */
export const put = function <T>(url: string, data = {}) {
  return request<any, AxiosResponseRewrite<T>>({
    method: 'PUT',
    url,
    data
  })
}

/**
 * Make a PATCH request
 * @param {String} url - The request URL
 * @param {Object} [data] - The request payload
 * @returns {AxiosInstance} - Axios response instance
 */
export const patch = function <T>(url: string, data = {}, ext: any = {}) {
  return request<any, AxiosResponseRewrite<T>>({
    method: 'PATCH',
    url,
    data,
    ...ext
  })
}
/**
 * Make a GET request
 * @param {String} url - The request URL
 * @param {Object} [params] - The request parameters
 * @param {Object} [ext] - Additional configuration options
 * @returns {AxiosInstance} - Axios response instance
 */
export const get = function <T>(url: string, params = {}, ext?: any) {
  return request<any, AxiosResponseRewrite<T>>({
    method: 'GET',
    url,
    params,
    ...ext
  })
}

/**
 * Make a DELETE request
 * @param {String} url - The request URL
 * @param {Object} [params] - The request parameters
 * @param {Object} [ext] - Additional configuration options
 * @returns {AxiosInstance} - Axios response instance
 */
export const remove = function <T>(url: string, params = {}, ext?: any) {
  return request<any, AxiosResponseRewrite<T>>({
    method: 'DELETE',
    url,
    params,
    ...ext
  })
}

/**
 * Get file stream response
 * @param {String} url - The request URL
 * @param {Object} [params] - The request parameters
 * @return {*} - Returns the file stream response
 */
export const getStream = function(url: string, params = {}) {
  return get<any>(url, params, {
    responseType: 'arraybuffer' // Set request data type, returns blob parsable type
  })
}

export const postStream = function(url: string, data = {}, params = {}) {
  return post<any>(url, data, params, {
    responseType: 'arraybuffer' // Set request data type, returns blob parsable type
  })
}

const showNotification = (message: string, description: string, key?: string, show: boolean = true) => {
  if (show) {
    Notification.error({
      key,
      message: '',
      description
    })
  }
}

/**
 * Handle request errors
 * @param {Object} error - The error object
 * @returns {Promise<never>} - Rejected promise with error
 */
const errorHandler = (error: any) => {
  if (error.response) {
    const data = error.response.data
    const status = error.response.status
    if (data?.code === 'license required') {
     Modal.error({
        key: 'License',
        title: 'License expired or invalid',
        content: h(
          'a',
          {
            onClick: () =>
            {
              Modal.destroyAll?.();
              window.location.href = '/#/init-license';
            }
          },
          'Please update License'
        )
      })
    } else if (status === 403) {
      showNotification('Forbidden', (data.message + '').substr(0, 90), '403')
    } else if (status === 500) {
      showNotification('Server Side Error', (data.message + '').substr(0, 90), '500')
    } else if (status === 400) {
      showNotification('Request Error', (data.message + '').substr(0, 90), '400')
    } else if (status === 401) {
      showNotification('Unauthorized', 'User not logged in', '401')
      setTimeout(() => {
        cleanToken()
        router.replace({
          path: LoginPath
        })
      }, 0)
    } else if (status === 404) {
      const data = error?.response?.data
      const message = error?.response?.data?.message || `${data?.error} ${data?.path}`
      showNotification(error?.code, message, '404')
    }
  } else if (error.response === undefined) {
    if (error.message.includes('timeout')) {
      showNotification(error.message, 'API response timeout', undefined)
    } else {
      showNotification(error.message, (error.stack + '').substr(0, 90), undefined)
    }
  }
  return Promise.reject(error)
}

const generateRandomString = (length) => {
  var result = '';
  var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

// Add custom headers to requests
const addheader = () => {
  let nonce = generateRandomString(10)
  let timestamp = new Date().getTime()
  let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  let jmStr = `${timezone}|${timestamp}|${nonce}`
  let sign = md5Js(jmStr)
  let obj = {
    nonce, // 10-digit random alphanumeric string
    timestamp,
    timezone, // Client timezone
    sign // Signature md5(timezone|timestamp|nonce)
  }

  return obj
}


// request interceptor
request.interceptors.request.use(config => {
  // If token exists
  // Let each request carry custom token, modify according to actual situation
  let headerObj = addheader()
  
  Object.keys(headerObj).forEach(key => {
    config.headers![key] = headerObj[key]
  })
  
  const token = getToken()
  const isFilterUrl = filterApiUrl.some(url => config.url.includes(url))
  if (!token && !isFilterUrl) {
    setTimeout(() => {
      cleanToken()
      router.replace({
        path: LoginPath
      })
    }, 0)
    return config
  }
 
  config.headers![TOKEN_KEY] = token

  return config
}, errorHandler)

/**
 * Response interceptor
 * Process response data
 */
request.interceptors.response.use(response => {
  if (response.data instanceof ArrayBuffer) {
    return response
  } else {
    const { status, message } = response.data
    // Add business interface success judgment method, only need to check if response contains: success as true
    if (typeof response.data === 'object' && typeof response.data.success === 'undefined') {
      response.data.success = status === SUCCESS_CODE
    }

    // If returning file stream, then return value is response
    if (response.headers['content-type'] === 'application/octet-stream; charset=UTF-8' || response.headers['content-type'] === 'application/vnd.ms-excel;charset=UTF-8') {
      return response.data
    } else {
      return response.data
    }
  }
}, errorHandler)

export default {
  request,
  post,
  get,
  patch,
  put,
  remove,
  getStream,
  postStream
}
